/**
 * Web Push / FCM token capture for likes & matches.
 *
 * No Firebase project credentials are committed. Masco must set:
 *   Pages / Worker secrets:
 *     FCM_SERVER_KEY   — legacy FCM server key (or migrate to HTTP v1 later)
 *     VAPID_PRIVATE_KEY
 *   Pages vars / HTML meta:
 *     VAPID_PUBLIC_KEY — also expose as <meta name="un8-vapid-public" content="…">
 *
 * Until those exist: client still requests permission and stores a local stub
 * token; Worker send path no-ops with `configured: false`.
 */

import { apiJson } from "../api/client";
import { isServerMode } from "../api/backend";

const TOKEN_KEY = "un8.push.token.v1";
const PUSH_SW = "/push-sw.js";

function vapidPublicKey(): string {
  if (typeof document === "undefined") return "";
  const meta = document.querySelector('meta[name="un8-vapid-public"]');
  const fromMeta = meta?.getAttribute("content")?.trim() || "";
  const fromWindow =
    typeof (window as unknown as { __UN8_VAPID_PUBLIC_KEY__?: string }).__UN8_VAPID_PUBLIC_KEY__ ===
    "string"
      ? (window as unknown as { __UN8_VAPID_PUBLIC_KEY__: string }).__UN8_VAPID_PUBLIC_KEY__.trim()
      : "";
  return fromMeta || fromWindow;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export type PushSetupResult =
  | { ok: true; token: string; mode: "webpush" | "local-stub" }
  | { ok: false; reason: "unsupported" | "denied" | "no-vapid" | "failed"; detail?: string };

export function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

export function readStoredPushToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

async function registerPushWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register(PUSH_SW, { scope: "/" });
  } catch {
    return null;
  }
}

async function postToken(token: string, platform: "webpush" | "fcm-stub") {
  if (!isServerMode()) return;
  await apiJson("/api/push/register", "POST", { token, platform });
}

/**
 * Request notification permission and capture a push subscription (or local stub).
 * Safe to call repeatedly.
 */
export async function enablePushNotifications(): Promise<PushSetupResult> {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };

  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission === "denied") return { ok: false, reason: "denied" };
  if (permission !== "granted") return { ok: false, reason: "denied" };

  const vapid = vapidPublicKey();
  const reg = await registerPushWorker();

  if (vapid && reg) {
    try {
      const existing = await reg.pushManager.getSubscription();
      const sub =
        existing ||
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapid) as BufferSource,
        }));
      const token = JSON.stringify(sub.toJSON());
      localStorage.setItem(TOKEN_KEY, token);
      await postToken(token, "webpush");
      return { ok: true, token, mode: "webpush" };
    } catch (err) {
      return {
        ok: false,
        reason: "failed",
        detail: err instanceof Error ? err.message : "subscribe failed",
      };
    }
  }

  // No VAPID yet — keep a deterministic local stub so QA can see the path.
  const stub = `local-stub:${Date.now()}`;
  localStorage.setItem(TOKEN_KEY, stub);
  await postToken(stub, "fcm-stub");
  return { ok: true, token: stub, mode: "local-stub" };
}

/** Show a local notification when the app is open (foreground fallback). */
export function notifyInApp(title: string, body: string) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  try {
    new Notification(title, { body, icon: "/brand/un8-lockup.png", tag: "un8-match" });
  } catch {
    /* ignore */
  }
}
